import React, { useEffect } from 'react';
import { Form, Input, Modal } from 'antd';

/**
 * GlobalBillCodeForm - A reusable modal form for creating or updating Global Bill Codes.
 * 
 * @param {boolean} visible - controls modal visibility
 * @param {function} onCreate - callback when form is submitted successfully
 * @param {function} onCancel - callback when modal is closed
 * @param {object} record - the existing record to edit (null for creation)
 */
const GlobalBillCodeForm = ({ 
  visible, 
  onCreate, 
  onCancel, 
  record 
}) => {
  const [form] = Form.useForm();

  // Synchronize form fields when record or visibility changes
  useEffect(() => {
    if (visible) {
      if (record) {
        form.setFieldsValue({
          name: record.name,
          code: record.code,
        });
      } else {
        form.resetFields();
      }
    }
  }, [record, visible, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // On success, call the onCreate handler (which handles both add and update in the parent)
      await onCreate(values);
      form.resetFields();
    } catch (error) {
      console.error('Validation Failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={visible} // Ant Design 5 compatibility
      visible={visible} // Legacy compatibility
      title={record ? "Update Global Bill Code" : "Add Global Bill Code"}
      okText="Submit"
      cancelText="Cancel"
      onCancel={handleCancel}
      onOk={handleOk}
      width={700}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        name="global_bill_code_form"
        initialValues={record}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { 
              required: true, 
              message: 'Please enter the global bill name!' 
            },
            {
              min: 2,
              message: 'Name must be at least 2 characters long'
            }
          ]}
        >
          <Input placeholder="Enter bill code name" />
        </Form.Item>

        <Form.Item
          name="code"
          label="Code"
          rules={[
            { 
              required: true, 
              message: 'Please enter the code!' 
            },
            {
              pattern: /^\S+$/,
              message: 'Spaces are not allowed!'
            }
          ]}
        >
          <Input placeholder="Enter unique code" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GlobalBillCodeForm;